import { confirmAlert, toastify } from "@helpers/toast";
import {
    handleToken,
    removeEmptyFields
} from "@helpers/utils";
import { useEffect, useState } from "react";

import Swal from "sweetalert2";
import axios from "axios";
import themeConfig from "@configs/theme-config";

const useAxios = (config = {}) => {

    const {
        base,
        initialLoader = false,
        alertTypeOnSuccess = "alert",
        showAlert = true,
        alert = {}
    } = config;

    const BASE_URLS = {
        panel     : themeConfig.apiUrl.base,
        superAdmin: themeConfig.apiUrl.superAdmin
    };
    const baseURL = BASE_URLS[base];
    const [loading, setLoading] = useState(initialLoader);
    const [error, setError] = useState(null);
    const [cancelTokenSource, setCancelTokenSource] = useState(null);

    const cancelAllRequests = () => {
        if (cancelTokenSource) {
            cancelTokenSource.cancel("Request canceled due to session timeout");
        }
    };
    const customizedHeaders = {
        accept       : "application/json",
        authorization: `Bearer ${handleToken(base)}`
    };
    
    const axiosInstance = axios.create({
        ...config,
        baseURL,
        headers: customizedHeaders
    });

    // Request interceptor
    axiosInstance.interceptors.request.use(
        async (requestConfig) => {
            if (["patch", "delete"].includes(requestConfig.method) && showAlert) {
                await new Promise((resolve, reject) => {
                    confirmAlert({ 
                        ...alert,
                        action             : requestConfig.method,
                        showLoaderOnConfirm: true,
                        allowOutsideClick  : false,
                        preConfirm         : () => {
                            return new Promise((innerResolve) => {
                                requestConfig._confirmResolve = innerResolve;
                                resolve();
                            });
                        }
                    }).then(() => {
                        // User confirmed, do nothing here
                    }).catch(() => {
                        reject(new Error("Action canceled by user"));
                    });
                });
            }
    
            setLoading(true);
            // Create a new cancel token source for this request
            const source = axios.CancelToken.source();
            setCancelTokenSource(source);
            // Add the cancel token to the request config
            requestConfig.cancelToken = source.token;
            requestConfig.data = removeEmptyFields(requestConfig.data);
            requestConfig.params = removeEmptyFields(requestConfig.params);
            return requestConfig;
            
        },
        (error) => {
            setLoading(false);
            setError(error);
            return Promise.reject(error);
        }
    );

    // Response interceptor
    axiosInstance.interceptors.response.use(
        (response) => {
            // Call the stored resolve function if it exists (for PATCH/DELETE requests)
            if (response.config._confirmResolve) {
                response.config._confirmResolve();
                delete response.config._confirmResolve;
            }
            
            if (response.data.message && showAlert) {
                if (response.config.method !== "get") {
                    if (alertTypeOnSuccess === "toast") {
                        toastify({ title: response.data.message, type: "success" });
                    } else {
                        Swal.fire({
                            text             : response.data.message,
                            icon             : "success",
                            showConfirmButton: false,
                            timer            : 1000
                        });
                    }
                }
            }
            setLoading(false);
            return response.data;
        },
        (error) => {
            // Call the stored resolve function if it exists (for PATCH/DELETE requests)
            if (error.config && error.config._confirmResolve) {
                error.config._confirmResolve();
                delete error.config._confirmResolve;
            }
            
            if (
                error.response &&
        error.response.data &&
        error.response?.data?.error?.errorCode === "102"
            ) {
                confirmAlert({
                    title            : "Authentication token expired!",
                    text             : "Please re-login to continue using the dashboard",
                    icon             : "error",
                    showCancelButton : false,
                    confirmButtonText: "Logout",
                    allowOutsideClick: () => false,
                    preConfirm       : async () => {
                        const logoutUrl = "/logout";
                        window.location.href = `${logoutUrl}`;
                    }
                });
                // Session timeout error (error code 102)
                cancelAllRequests();
                // Handle session timeout error here (e.g., redirect to login)
                // ...
            } else {
                setError(error);
                if (
                    window.navigator.onLine &&
          error?.response?.data?.error?.errorCode !== "102"
                ) {
                    const errorMsg =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            "Something went wrong. Please try in sometime.";
                    if (!["ERR_NETWORK", "ERR_CANCELED"].includes(error.code)) {
                        toastify({ title: errorMsg, type: "error" });
                        setLoading(false);
                    }
                    /* The line `// }` is commented out, which means it is not being executed as part of
                the code. It is likely a typo or a leftover comment that was not removed. */
                }
            }
            return Promise.reject(error);
        }
    );

    useEffect(() => {
        const source = axios.CancelToken.source();
        setCancelTokenSource(source);

        return () => {
            source.cancel("Request canceled due to component unmount");
        };
    }, []);

    return {
        axiosInstance,
        loading,
        error,
        cancelAllRequests,
        setLoading
    };
};

export default useAxios;
